import { Controller } from "@hotwired/stimulus"

const EASYMDE_JS_URL = "https://cdn.jsdelivr.net/npm/easymde@2.18.0/dist/easymde.min.js"
const EASYMDE_CSS_URL = "https://cdn.jsdelivr.net/npm/easymde@2.18.0/dist/easymde.min.css"

// Loads EasyMDE on demand and caches the in-flight request so multiple editors
// on a page (and repeat Turbo visits) reuse a single download. Initializing the
// editor inside connect() without awaiting this races the async CDN script: on a
// Turbo navigation connect() can run before EasyMDE is defined, which threw and
// left the toolbar missing and the textarea un-enhanced.
let easyMDELoader = null

function loadEasyMDE() {
  if (window.EasyMDE) return Promise.resolve()
  if (easyMDELoader) return easyMDELoader

  easyMDELoader = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${EASYMDE_CSS_URL}"]`)) {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = EASYMDE_CSS_URL
      document.head.appendChild(link)
    }

    const script = document.createElement("script")
    script.src = EASYMDE_JS_URL
    script.onload = () => resolve()
    script.onerror = () => {
      easyMDELoader = null
      reject(new Error("Failed to load EasyMDE"))
    }
    document.head.appendChild(script)
  })

  return easyMDELoader
}

export default class extends Controller {
  static targets = ["editor"]
  static values = {
    imageUpload: { type: Boolean, default: false },
    uploadEndpoint: { type: String, default: "" }
  }

  connect() {
    this.hasUnsavedChanges = false
    this.easyMDE = null
    this.isActive = true

    this.initializeEditor()
    this.bindFormEvents()
    this.bindNavigationEvents()
  }

  disconnect() {
    this.isActive = false
    this.cleanup()
  }

  initializeEditor() {
    if (!this.hasEditorTarget) return

    loadEasyMDE()
      .then(() => {
        if (!this.isActive || !this.hasEditorTarget) return

        const options = { element: this.editorTarget }

        if (this.imageUploadValue) {
          const csrfToken = document.querySelector("[name='csrf-token']")?.content
          Object.assign(options, {
            showIcons: ['table', 'heading-2', 'heading-3', 'heading-4', 'horizontal-rule'],
            promptURLs: true,
            hideIcons: ['heading'],
            uploadImage: true,
            previewImagesInEditor: true,
            imagePathAbsolute: true,
            imageCSRFToken: csrfToken,
            imageCSRFName: "X-CSRF-Token",
            imageCSRFHeader: true,
            imageUploadEndpoint: this.uploadEndpointValue
          })
        }

        this.easyMDE = new EasyMDE(options)
        this.initialContent = this.easyMDE.value()

        this.easyMDE.codemirror.on('change', () => {
          this.hasUnsavedChanges = this.easyMDE.value() !== this.initialContent
        })
      })
      .catch((error) => {
        console.error(error)
      })
  }

  bindFormEvents() {
    this.element.querySelectorAll('input, select, textarea').forEach(el => {
      el.addEventListener('change', this.markAsChanged)
    })

    this.element.addEventListener('submit', this.clearChanges)
  }

  bindNavigationEvents() {
    document.addEventListener('turbo:before-visit', this.handleTurboBeforeVisit)
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    document.addEventListener('turbo:before-cache', this.handleBeforeCache)
  }

  markAsChanged = () => {
    this.hasUnsavedChanges = true
  }

  clearChanges = () => {
    this.hasUnsavedChanges = false
  }

  handleTurboBeforeVisit = (e) => {
    if (this.hasUnsavedChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to leave?')) {
        e.preventDefault()
      }
    }
  }

  handleBeforeUnload = (e) => {
    if (this.hasUnsavedChanges) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  handleBeforeCache = () => {
    this.cleanup()
  }

  cleanup() {
    document.removeEventListener('turbo:before-visit', this.handleTurboBeforeVisit)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache)
    this.hasUnsavedChanges = false

    if (this.easyMDE) {
      this.easyMDE.toTextArea()
      this.easyMDE = null
    }
  }
}
